/**
 * SucursalSelectorModal.tsx — Selector de sucursal post-login.
 *
 * Réplica de la pantalla original (puntodeventa.online → Account/Login.cshtml
 * #content-sucursales + login.js showBranches): título "Seleccione una sucursal",
 * buscador "Buscar sucursal..", y una GRILLA DE CARDS — cada card con el logo de
 * la sucursal, el nombre (negrita), la empresa (gris) y una estrella si es la
 * sucursal por defecto. El usuario puede tener cientos (p.ej. ≈194), por eso es
 * buscable y con scroll. Con una sola sucursal no se muestra (se autoselecciona).
 */
import { useMemo, useState } from 'react';
import type { BranchInfo } from '../../lib/auth';

/** Host donde viven los logos de marca (públicos). Vacío en logos faltantes. */
const ASSET_BASE = 'https://puntodeventa.online';
const FALLBACK_LOGO = '/kapi.png';

function logoSrc(logo?: string): string {
  if (!logo) return FALLBACK_LOGO;
  if (/^https?:\/\//i.test(logo)) return logo;
  return ASSET_BASE + (logo.startsWith('/') ? logo : `/${logo}`);
}

export interface SucursalSelectorModalProps {
  open: boolean;
  branches: BranchInfo[];
  /** Sucursal actualmente activa (para resaltarla), si la hay */
  currentId?: string | null;
  /** Si false, se puede cerrar (modo "cambiar sucursal"); si true, es obligatorio */
  mandatory?: boolean;
  onSelect: (branchId: string) => void;
  onClose?: () => void;
}

export function SucursalSelectorModal({
  open,
  branches,
  currentId,
  mandatory = true,
  onSelect,
  onClose,
}: SucursalSelectorModalProps): React.ReactElement | null {
  const [query, setQuery] = useState('');

  // Orden como el original: default(s) primero, luego por empresa y nombre.
  const ordered = useMemo(() => {
    return [...branches].sort((a, b) => {
      if (Boolean(a.isDefault) !== Boolean(b.isDefault)) return a.isDefault ? -1 : 1;
      const c = (a.companyName ?? '').localeCompare(b.companyName ?? '', 'es');
      return c !== 0 ? c : a.name.localeCompare(b.name, 'es');
    });
  }, [branches]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        (b.companyName ?? '').toLowerCase().includes(q) ||
        b.id.toLowerCase().includes(q),
    );
  }, [ordered, query]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[88vh]">
        {/* Encabezado: logo Kapi + título + buscador (igual al original) */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between gap-3">
            <img src="/kapi.png" alt="Kapi" className="h-7 w-auto" />
            {!mandatory && onClose && (
              <button
                type="button"
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
                aria-label="Cerrar"
              >
                ×
              </button>
            )}
          </div>
          <h5 className="text-center text-slate-500 font-semibold mt-3 mb-3">Seleccione una sucursal</h5>
          <div className="relative max-w-md mx-auto">
            <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
            <input
              autoFocus
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar sucursal.."
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Grilla de cards */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-400 text-sm py-10">Sin coincidencias para “{query}”.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {filtered.map((b) => {
                const isCurrent = b.id === currentId;
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => onSelect(b.id)}
                    title={b.companyName ? `${b.companyName} — ${b.name}` : b.name}
                    className={`relative text-left bg-white rounded-xl border p-2.5 flex items-center gap-3 transition-all hover:shadow-md ${
                      isCurrent ? 'border-blue-500 ring-1 ring-blue-500' : 'border-slate-200 hover:border-blue-300'
                    }`}
                  >
                    {b.isDefault && (
                      <span className="absolute top-1 left-1 text-amber-400" title="Sucursal por defecto">
                        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 17.6 5.9 20.4l1.4-6.8L2.2 9l6.9-.7z" /></svg>
                      </span>
                    )}
                    <span className="w-11 h-11 shrink-0 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden">
                      <img
                        src={logoSrc(b.logo)}
                        alt=""
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          const img = e.currentTarget;
                          if (img.src !== window.location.origin + FALLBACK_LOGO) img.src = FALLBACK_LOGO;
                        }}
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-800 truncate">{b.name}</span>
                      {b.companyName && (
                        <span className="block text-xs text-slate-400 truncate">{b.companyName}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SucursalSelectorModal;
